import { Carousel } from '@/components/ui/Carousel'
import { subtitlePreviewUrl, useSubtitleStyles } from '@/api/endpoints/media'
import { cn } from '@/lib/cn'
import { CaptionColorPicker } from './CaptionColorPicker'
import styles from './CaptionStyleCarousel.module.scss'

/**
 * Styles that fall back to the legacy MoviePy compositing path because
 * their PIL effects (Gaussian blur for neon_blur, per-glyph vertical
 * gradient for gradient_fill) don't map cleanly to libass's ASS
 * primitives. Picking one of these multiplies the composing time
 * roughly 30-40x vs. the FFmpegEngine path. Keep in sync with the
 * backend's ``ASS_SUPPORTED_STYLES`` (anything outside it is "slow").
 */
const SLOW_STYLES = new Set(['neon_blur', 'gradient_fill'])

/** O único estilo que lê a cor da palavra sendo lida. */
const KARAOKE = 'karaoke'

export interface CaptionStyleCarouselProps {
  value: string
  onValueChange: (value: string) => void
  /**
   * If true, only renders the cards. Default: also shows a header card
   * for ``random`` (the meta-option) at the start, distinguished by a
   * shimmer treatment so it reads as "varies per video".
   */
  hideRandom?: boolean
  /**
   * Cor da palavra sendo lida no estilo ``karaoke`` (``#RRGGBB``; vazio = o
   * vermelho padrão do backend). Vai para a prévia do card, e o seletor de
   * cor aparece embaixo do carrossel quando o karaokê está escolhido e
   * ``onActiveColorChange`` foi passado.
   */
  activeColor?: string
  onActiveColorChange?: (color: string) => void
}

/**
 * Carousel of subtitle-style preview cards. Each card is the **real PNG**
 * the renderer would produce — fetched from
 * ``GET /api/v1/media/subtitle-preview?style=...`` — so the picker is
 * WYSIWYG. The catalog itself comes from
 * ``GET /api/v1/media/subtitle-styles``, which introspects the backend
 * ``STYLES`` dict, so adding a new style on the server makes it appear
 * here without code changes.
 */
export function CaptionStyleCarousel({
  value,
  onValueChange,
  hideRandom = false,
  activeColor,
  onActiveColorChange,
}: CaptionStyleCarouselProps) {
  const { data: catalog = [], isPending } = useSubtitleStyles()

  if (isPending) {
    return (
      <div className={styles.skeletonRow} aria-busy>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={styles.skeletonCard} />
        ))}
      </div>
    )
  }

  const items = hideRandom ? catalog.filter((s) => s.key !== 'random') : catalog
  const isSlowSelected = SLOW_STYLES.has(value)

  return (
    <div>
      <Carousel itemWidth={146} gap={10} aria-label="Estilos de legenda">
        {items.map((opt) => {
          const isActive = opt.key === value
          const isRandom = opt.key === 'random'
          const isSlow = SLOW_STYLES.has(opt.key)
          return (
            <button
              key={opt.key}
              type="button"
              className={cn(styles.card, isActive && styles.active)}
              onClick={() => {
                onValueChange(opt.key)
              }}
              aria-pressed={isActive}
            >
              <div className={styles.preview}>
                {isRandom ? (
                  <div className={styles.randomFace} aria-hidden>
                    <span>?</span>
                  </div>
                ) : (
                  <img
                    className={styles.previewImg}
                    src={subtitlePreviewUrl(opt.key, opt.key === KARAOKE ? activeColor : undefined)}
                    alt={`Prévia do estilo ${opt.label}`}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                )}
                {isSlow ? (
                  <span className={styles.slowBadge} title="Renderização lenta">
                    Lento
                  </span>
                ) : null}
              </div>
              <div className={styles.name}>{opt.label}</div>
            </button>
          )
        })}
      </Carousel>
      {value === KARAOKE && onActiveColorChange ? (
        <div className={styles.colorInset}>
          <CaptionColorPicker value={activeColor ?? ''} onValueChange={onActiveColorChange} />
        </div>
      ) : null}
      {isSlowSelected ? (
        <p className={styles.slowNotice} role="status">
          ⚠️ Esse estilo usa renderização pixel-a-pixel (Gaussian blur ou gradiente
          por glifo) e cai no caminho lento (MoviePy). A composição leva ~30× mais
          tempo (≈10s → ≈400s no vídeo todo). Escolha outro estilo se quiser a
          geração rápida.
        </p>
      ) : null}
    </div>
  )
}
